import React, { useCallback, useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api"
import { Flex, Spin } from "antd"
import ZoomButtons from "components/map/ZoomButtons"
import useGeocoder from "modules/google/hooks/useGeocoder"
import { API_OPTIONS, DEFAULT_LOCATION, MAP_OPTIONS, MARKER_ICON } from "constants/map"
import { useDevice } from "hooks/useDevice"
import styles from "./MapPinPicker.module.css"

const DEFAULT_CENTER = { lat: DEFAULT_LOCATION.lat, lng: DEFAULT_LOCATION.lng }
const DEFAULT_ZOOM_OUT = DEFAULT_LOCATION.zoom
const PINNED_ZOOM = 15

export default function MapPinPicker({ latitude, longitude, onLocationChange }) {
  const { isLoaded } = useJsApiLoader(API_OPTIONS)
  const { isMobile } = useDevice()
  const geocoder = useGeocoder()
  const mapRef = useRef(null)

  const hasPinnedCoords = latitude && longitude
  const [markerPos, setMarkerPos] = useState(hasPinnedCoords ? { lat: Number(latitude), lng: Number(longitude) } : null)
  const [center, setCenter] = useState(hasPinnedCoords ? { lat: Number(latitude), lng: Number(longitude) } : DEFAULT_CENTER)
  const [zoom, setZoom] = useState(hasPinnedCoords ? PINNED_ZOOM : DEFAULT_ZOOM_OUT)

  useEffect(() => {
    if (latitude && longitude) {
      const pos = { lat: Number(latitude), lng: Number(longitude) }
      setMarkerPos(pos)
      setCenter(pos)
      setZoom(PINNED_ZOOM)
      if (mapRef.current) {
        mapRef.current.panTo(pos)
        mapRef.current.setZoom(PINNED_ZOOM)
      }
    }
  }, [latitude, longitude])

  const pinLocation = useCallback(
    (pos) => {
      setMarkerPos(pos)
      setCenter(pos)
      setZoom(PINNED_ZOOM)
      geocoder({ location: pos }, (place) => {
        if (place) {
          onLocationChange({
            lat: pos.lat,
            lng: pos.lng,
            formattedAddress: place.formatted_address,
            addressComponents: place.address_components
          })
        }
      })
    },
    [geocoder, onLocationChange]
  )

  const handleMapEvent = useCallback(
    (e) => pinLocation({ lat: e.latLng.lat(), lng: e.latLng.lng() }),
    [pinLocation]
  )

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map
  }, [])

  if (!isLoaded) {
    return (
      <Flex align="center" justify="center" className={styles.loadingWrapper}>
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <div className={styles.wrapper}>
      <GoogleMap
        mapContainerClassName={styles.map}
        mapContainerProps={{ "aria-label": "Location picker map" }}
        center={center}
        zoom={zoom}
        options={{ ...MAP_OPTIONS, scrollwheel: !isMobile }}
        onLoad={handleMapLoad}
        onClick={handleMapEvent}
      >
        {markerPos && (
          <Marker
            position={markerPos}
            // eslint-disable-next-line no-undef
            icon={{ ...MARKER_ICON, scaledSize: new window.google.maps.Size(30, 30) }}
            title="Drag to adjust location"
            draggable
            onDragEnd={handleMapEvent}
          />
        )}
        <Flex vertical align="flex-end" justify="flex-end" className={styles.zoomOverlay}>
          <ZoomButtons />
        </Flex>
      </GoogleMap>
      <span className={styles.hint}>
        {isMobile ? "Tap the map or drag the pin to set your location" : "Click on the map or drag the pin to set your location"}
      </span>
    </div>
  )
}

MapPinPicker.propTypes = {
  latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onLocationChange: PropTypes.func.isRequired
}
